<template>
  <div class="bundle-container" :class="['dy-variation']">
    <div class="bundle-title">
      <template v-if="bundlingOptions?.custom_bundle_header">
        {{ bundlingOptions.custom_bundle_header }}
      </template>
      <template v-else> Bundle & Save </template>
    </div>
    <div class="bundle-desc">
      <template v-if="bundlingOptions?.custom_bundle_subheader">
        {{ bundlingOptions?.custom_bundle_subheader }}
      </template>
      <template v-else-if="customBundleSavings">
        Save an extra {{ currencySymbol + customBundleSavings }} off each accessory when purchased with a {{ name }}.
      </template>
    </div>

    <BundleTile
      v-for="(item, index) in props?.accessories"
      :key="index + '_product'"
      :product="item"
      :is-current-product="checkIfCurrentProduct(item?.product_offerings?.bc_primary_product?.product?.id)"
      :per-item-bundle-savings="customBundleSavings"
      @bundle-tile-option-selected="bundleTileOptionSelected($event, index)"
    >
      <CheckRadio
        :aria-label="`Select ${item.title}`"
        :value="checkedByDefault ? 'true' : 'false'"
        :is-default="checkedByDefault"
        :check-override="checkedByDefault"
        :override-value="checkedByDefault"
        :disabled="checkIfCurrentProduct(item?.product_offerings?.bc_primary_product?.product?.id)"
        emit-input-change="item-toggle"
        @item-toggle="selectItem(index)"
      >
        {{ '' }}
      </CheckRadio>
    </BundleTile>

    <div class="bundle-pricing">
      <div class="bundle-subtotal">
        {{ useTranslate('CartSlideout.subtotal') }}:
        {{ currencySymbol + (totalBundlePricing?.saleTotal - customBundleSavingsTotal).toFixed(2) }}
        <span class="strikethrough">{{ currencySymbol + totalBundlePricing?.origTotal?.toFixed(2) }}</span
        ><span v-if="!localeStore?.isUsLocale" class="msrp-price">(MSRP)</span>
      </div>
      <div class="bundle-savings">
        {{
          useTranslate('GLOBALS.youSave') +
          ' ' +
          currencySymbol +
          (totalBundlePricing?.origTotal - totalBundlePricing?.saleTotal + customBundleSavingsTotal).toFixed(2)
        }}
      </div>
    </div>

    <AddItemsToCartButton
      :add-to-cart-msg="`Add ${customBundleNumberSelected} Item${
        (customBundleNumberSelected || 0) > 1 ? 's' : ''
      } to Cart`"
      :products="selectedAccessories"
      :selected-option-ids="state.bundleTileOptionIds"
    />
  </div>
</template>

<script setup lang="ts">
import { ProductV2 } from '@solo-stove/types/contentstack/stove/product';
import { useLocaleStore } from '~/store/locale';
import { useProductDataStore } from '~/store/product-data';

const props = defineProps({
  product: {
    type: Object as () => ProductV2,
    required: true,
  },
  accessories: {
    type: Array as () => any[],
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
});

const localeStore = useLocaleStore();

const checkedByDefault = ref(props.product?.product_bundling_options?.custom_bundle_checked_by_default);

const state = reactive({
  accessoriesSelected: props?.accessories?.length
    ? new Array(props?.accessories?.length).fill(checkedByDefault?.value)
    : [],
  bundleTileOptionIds: props?.accessories?.length
    ? props?.accessories.map((item: any) =>
        item?.product_offerings?.product_variations?.length
          ? item?.product_offerings?.product_variations[0]?.bc_variant?.id
          : null
      )
    : [],
});

const selectedAccessories = computed<any[]>(() => {
  return props?.accessories?.length
    ? props?.accessories
        ?.map((product: any, index: number) => {
          // add tileIndex for multi-a2c component to find mathcing variants
          return {
            ...product,
            tileIndex: index,
          };
        })
        .filter((product: any, index: number) => state.accessoriesSelected[index])
    : [];
});

const totalBundlePricing = computed(() => {
  let origTotal = 0;
  let saleTotal = 0;
  if (props?.accessories?.length) {
    for (let i = 0; i < props?.accessories.length; i++) {
      const variants = props?.accessories[i]?.product_offerings?.product_variations;
      origTotal += state.accessoriesSelected[i] ? getVariantPrice(variants, i, 'price') || 0 : 0;
      saleTotal += state.accessoriesSelected[i] ? getVariantPrice(variants, i, 'calculated_price') || 0 : 0;
    }
  }
  return {
    origTotal,
    saleTotal,
  };
});

const currencySymbol = computed(() => localeStore.currencySymbol);

const baseProduct = computed(() => props.product?.product_offerings?.bc_primary_product?.product);
const bundlingOptions = computed(() => props.product?.product_bundling_options);

const customBundleSavings = computed(() => bundlingOptions?.value?.custom_bundle_savings_per_item || 0);

const customBundleNumberSelected = computed<number>(() => {
  const numTrue = state.accessoriesSelected?.filter((isChecked: any) => isChecked);
  return numTrue?.length;
});

const customBundleSavingsTotal = computed<number>(() => {
  // TODO - update this logic if savings doesn't require main product to be selected
  // subtract 1 since current item doesnt count towards savings
  return customBundleNumberSelected.value ? (customBundleNumberSelected.value - 1) * customBundleSavings?.value : 0;
});

function getVariantPrice(variants: any[], index: number, objectSelector: string) {
  const foundMatch = variants?.find((variant: any) => {
    return variant?.bc_variant?.id === state.bundleTileOptionIds[index];
  });
  const skuMatch = foundMatch?.bc_variant?.sku;
  const priceInfo = useProductDataStore().getPriceBySku(skuMatch);
  const price = priceInfo?.[objectSelector];
  return price;
}
// function resetSelectedAccessories(): void {
//   state.accessoriesSelected.length = 0;
//   state.accessoriesSelected = new Array(props?.accessories?.length).fill(true);
// }
function bundleTileOptionSelected(variant: any, index: any) {
  state.bundleTileOptionIds[index] = variant?.id;
}
function selectItem(index: number) {
  state.accessoriesSelected[index] = !state.accessoriesSelected[index];
}
function checkIfCurrentProduct(id: number): boolean {
  return baseProduct.value.id === id;
}
</script>

<style lang="scss" scoped>
.bundle-container {
  margin-top: 1rem;
  margin-bottom: 3rem;
  line-height: 150%;
  color: $color-neutral-cool-900;
  .bundle-title {
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 1.25rem;
  }
  .bundle-desc {
    font-size: 1rem;
    margin-bottom: 20px;
  }
  .bundle-pricing {
    margin-top: #{local-functions.rem-calc(25)};
    margin-bottom: 1.5rem;
    font-weight: 500;
    font-size: 0.875rem;
    .bundle-subtotal {
      font-size: 1.125rem;
    }
    .bundle-savings {
      color: #c90c38;
    }
    .bundle-total-savings {
      color: $color-success-dark;
    }
    .strikethrough {
      font-weight: 500;
      font-size: 0.875rem;
    }
    .msrp-price {
      margin-left: 4px;
      font-size: 0.75rem;
      font-weight: 500;
      color: #6a6c77;
    }
  }
  @include local-mixins.tablet_and_mobile {
    .bundle-title {
      font-size: #{local-functions.rem-calc(22)};
    }
    .bundle-desc {
      font-size: 0.875rem;
    }
  }
}
</style>
